import React, { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Send, Loader2, MessageCircle, User,
  Stethoscope, Check, CheckCheck
} from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MessagesPage() {
  const navigate = useNavigate();
  const { recipientId } = useParams();
  const [searchParams] = useSearchParams();
  const initialRecipient = recipientId || searchParams.get('with');
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    
    // Set up polling for new messages
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (initialRecipient && conversations.length > 0) {
      const conv = conversations.find(c => c.participant_id === initialRecipient);
      if (conv) {
        selectConversation(conv);
      } else {
        // Create new conversation with this recipient
        fetchConversationWith(initialRecipient);
      }
    }
  }, [initialRecipient, conversations]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/messages/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
        setTotalUnread(data.total_unread || 0);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversationWith = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/messages/conversation/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedConversation({
          participant_id: userId,
          participant_name: data.participant_name,
          participant_role: data.participant_role,
          participant_picture: data.participant_picture
        });
        setMessages(data.messages || []);
        
        // Mark as read
        if (data.unread_count > 0) {
          markConversationRead(userId);
        }
      }
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
    }
  };

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    fetchConversationWith(conv.participant_id);
    inputRef.current?.focus();
  };

  const markConversationRead = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/messages/read/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Refresh conversations to update unread counts
      fetchConversations();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/messages/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recipient_id: selectedConversation.participant_id,
          content: newMessage.trim()
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, {
          message_id: data.message_id,
          content: data.content,
          message_type: 'text',
          is_own: true,
          sender_name: 'You',
          created_at: data.created_at,
          status: data.status
        }]);
        setNewMessage('');
        fetchConversations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white" data-testid="messages-page">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="flex-shrink-0"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        
        {selectedConversation ? (
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              {selectedConversation.participant_picture ? (
                <img 
                  src={selectedConversation.participant_picture} 
                  alt={selectedConversation.participant_name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : selectedConversation.participant_role === 'dietitian' ? (
                <Stethoscope className="w-5 h-5 text-orange-600" />
              ) : (
                <User className="w-5 h-5 text-orange-600" />
              )}
            </div>
            <div>
              <h1 className="font-semibold text-gray-900">{selectedConversation.participant_name}</h1>
              <p className="text-xs text-gray-500 capitalize">{selectedConversation.participant_role}</p>
            </div>
          </div>
        ) : (
          <h1 className="font-semibold text-gray-900 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-orange-500" />
            Messages
            {totalUnread > 0 && (
              <Badge className="bg-orange-500">{totalUnread}</Badge>
            )}
          </h1>
        )}
      </div>

      <div className="max-w-4xl mx-auto flex h-[calc(100vh-60px)]">
        {/* Conversations List - Hidden on mobile when conversation selected */}
        <div className={`${selectedConversation ? 'hidden md:block' : ''} w-full md:w-80 border-r bg-white overflow-y-auto`}>
          {conversations.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No conversations yet</p>
              <p className="text-sm mt-1">Start a conversation with your care team</p>
            </div>
          ) : (
            <div className="divide-y">
              {conversations.map(conv => (
                <button
                  key={conv.participant_id}
                  className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                    selectedConversation?.participant_id === conv.participant_id ? 'bg-orange-50' : ''
                  }`}
                  onClick={() => selectConversation(conv)}
                  data-testid={`conversation-${conv.participant_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                        {conv.participant_picture ? (
                          <img 
                            src={conv.participant_picture} 
                            alt={conv.participant_name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : conv.participant_role === 'dietitian' ? (
                          <Stethoscope className="w-6 h-6 text-orange-600" />
                        ) : (
                          <User className="w-6 h-6 text-orange-600" />
                        )}
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-gray-900 truncate">{conv.participant_name}</p>
                        <span className="text-xs text-gray-500">{formatTime(conv.last_message_at)}</span>
                      </div>
                      <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                        {conv.is_last_own && 'You: '}
                        {conv.last_message}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Messages Area */}
        {selectedConversation ? (
          <div className="flex-1 flex flex-col bg-gray-50">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No messages yet</p>
                  <p className="text-sm">Send the first message!</p>
                </div>
              ) : (
                messages.map(msg => (
                  <div
                    key={msg.message_id}
                    className={`flex ${msg.is_own ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] ${msg.is_own ? 'order-2' : 'order-1'}`}>
                      <div className={`px-4 py-2 rounded-2xl ${
                        msg.is_own 
                          ? 'bg-orange-500 text-white rounded-br-md' 
                          : 'bg-white text-gray-900 rounded-bl-md shadow-sm'
                      }`}>
                        <p className="text-sm">{msg.content}</p>
                      </div>
                      <div className={`flex items-center gap-1 mt-1 text-xs text-gray-400 ${
                        msg.is_own ? 'justify-end' : 'justify-start'
                      }`}>
                        <span>{formatTime(msg.sent_at)}</span>
                        {msg.is_own && (
                          msg.status === 'read' ? (
                            <CheckCheck className="w-3 h-3 text-blue-500" />
                          ) : (
                            <Check className="w-3 h-3" />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="p-4 bg-white border-t">
              <form 
                onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
                className="flex items-center gap-2"
              >
                <Input
                  ref={inputRef}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1"
                  data-testid="message-input"
                />
                <Button
                  type="submit"
                  size="icon"
                  className="bg-orange-500 hover:bg-orange-600"
                  disabled={!newMessage.trim() || sending}
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </form>
            </div>
          </div>
        ) : (
          <div className="hidden md:flex flex-1 items-center justify-center bg-gray-50">
            <div className="text-center text-gray-500">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Select a conversation</p>
              <p className="text-sm">or start a new one with your care team</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
